import { PROFILE_VIEW_SECTION } from '@workspace/seedforge-shared'
import { Avatar, AvatarFallback, AvatarImage } from '@workspace/ui/components/avatar'
import { getInitials } from '@workspace/ui/lib/utils'
import { GraduationCap, Linkedin, Loader2 } from 'lucide-react'
import type { ReactNode } from 'react'
import { Section } from '../components/section.js'
import type { DataRoomTeamMember } from '../types.js'

const MAX_EXPERIENCES = 3

export interface TeamSectionProps {
  team: DataRoomTeamMember[]
  onLinkedInClick?: (memberName: string) => void
}

export function TeamSection({ team, onLinkedInClick }: TeamSectionProps) {
  return (
    <Section title="Team" dataSection={PROFILE_VIEW_SECTION.TEAM}>
      {team.length === 0 ? (
        <p className="text-sm text-muted-foreground/50">No team members added yet.</p>
      ) : (
        <div className="grid grid-cols-1 items-start gap-3 sm:grid-cols-2">
          {team.map((member) => {
            const isScraping = member.linkedinScrapeStatus === 'SCRAPING'
            const sortedLogos = member.companyLogos
              ? [...member.companyLogos]
                  .filter((c) => !c.isCurrent)
                  .sort((a, b) => a.order - b.order)
                  .slice(0, MAX_EXPERIENCES)
              : []
            const hasEducation = Boolean(
              member.lastEducation?.schoolName || member.lastEducation?.degree
            )

            const linkWrap = (node: ReactNode, extraClass?: string): ReactNode => {
              if (!member.linkedInUrl) return node
              return (
                <a
                  href={member.linkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onLinkedInClick?.(member.name)}
                  className={extraClass}
                >
                  {node}
                </a>
              )
            }

            return (
              <div
                key={member.name}
                className="relative flex h-full flex-col rounded-lg bg-card p-4"
              >
                <div className="flex items-start gap-4">
                  {member.linkedInUrl && (
                    <a
                      href={member.linkedInUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => onLinkedInClick?.(member.name)}
                      aria-label={`Open ${member.name}'s LinkedIn profile`}
                      className="absolute right-3 top-3 flex size-7 items-center justify-center rounded-md bg-[#0a66c2] text-white transition-transform hover:scale-110"
                    >
                      <Linkedin className="size-4" fill="currentColor" strokeWidth={0} />
                    </a>
                  )}
                  <Avatar className="size-14 shrink-0 bg-elevated">
                    {member.imageUrl && <AvatarImage src={member.imageUrl} alt={member.name} />}
                    <AvatarFallback className="bg-elevated text-sm font-semibold text-muted-foreground">
                      {getInitials(member.name)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0 flex-1">
                    {linkWrap(
                      <div className="truncate text-base font-medium text-white hover:underline">
                        {member.name}
                      </div>
                    )}
                    {member.role && (
                      <div className="truncate text-sm text-muted-foreground/50">{member.role}</div>
                    )}
                  </div>
                </div>

                {sortedLogos.length > 0 && (
                  <ul className="mt-3 flex w-full flex-col gap-2 border-t border-border pt-3">
                    {sortedLogos.map((c) => (
                      <li
                        key={`${member.id ?? member.name}-${c.order}`}
                        className="flex items-start gap-3"
                      >
                        <img
                          src={c.logoUrl}
                          alt={c.companyName ?? ''}
                          className="mt-0.5 size-8 shrink-0 rounded object-contain"
                          loading="lazy"
                        />
                        <div className="min-w-0 flex-1">
                          <div className="flex items-baseline gap-2">
                            {c.companyName &&
                              linkWrap(
                                <span className="truncate text-sm font-medium text-white hover:underline">
                                  {c.companyName}
                                </span>,
                                'block min-w-0 flex-1 truncate'
                              )}
                            {c.duration && (
                              <span className="shrink-0 text-xs text-muted-foreground/50">
                                {c.duration}
                              </span>
                            )}
                          </div>
                          {c.position && (
                            <div className="truncate text-xs text-muted-foreground">
                              {c.position}
                            </div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}

                {hasEducation && (
                  <div className="mt-3 flex items-start gap-3 border-t border-border pt-3">
                    {member.lastEducation?.logoUrl ? (
                      <img
                        src={member.lastEducation.logoUrl}
                        alt={member.lastEducation.schoolName ?? ''}
                        className="mt-0.5 size-8 shrink-0 rounded object-contain"
                        loading="lazy"
                      />
                    ) : (
                      <GraduationCap className="mt-0.5 size-5 shrink-0 text-muted-foreground/50" />
                    )}
                    <div className="min-w-0 flex-1">
                      {member.lastEducation?.schoolName && (
                        <div className="truncate text-sm font-medium text-white">
                          {member.lastEducation.schoolName}
                        </div>
                      )}
                      {member.lastEducation?.degree && (
                        <div className="truncate text-xs text-muted-foreground">
                          {member.lastEducation.degree}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {isScraping && (
                  <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-card/70 backdrop-blur-[1px]">
                    <div className="flex items-center gap-2 rounded-md bg-background/80 px-3 py-1.5 text-xs font-medium text-muted-foreground">
                      <Loader2 className="size-3.5 animate-spin" />
                      Refreshing LinkedIn…
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </Section>
  )
}
